import React, { useState, useEffect } from 'react'
import styles from './Login.module.css'
import { useDispatch } from 'react-redux'
import { useHistory, Link } from 'react-router-dom'
import { signin } from '../../actions/auth'
import { useSnackbar } from 'react-simple-snackbar'

const initialState = {
  email: '',
  password: '',
}

const IconExcel = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
  </svg>
)

const IconMail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

const IconPdf = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M10 12h4M10 16h4" />
  </svg>
)

const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const IconChart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
)

const MODULES = [
  {
    id: 'rh',
    badge: 'Module RH',
    title: 'Facturation Coaches',
    accent: 'rh',
    symbol: 'RH',
    flow: ['Import Excel', 'Factures PDF', 'Envoi email'],
    items: [
      { step: '01', icon: <IconExcel />, label: 'Import mensuel', text: 'Fichier Excel RH → génération automatique des factures coaches' },
      { step: '02', icon: <IconMail />, label: 'Distribution', text: 'Envoi groupé ou ciblé des PDF par email' },
      { step: '03', icon: <IconChart />, label: 'Suivi', text: 'Campagnes, statuts d\'envoi et historique complet' },
    ],
  },
  {
    id: 'corporate',
    badge: 'Module Corporate',
    title: 'Conventions Clients',
    accent: 'corporate',
    symbol: 'Corp.',
    flow: ['PDF Sage X3', 'Matching client', 'Envoi convention'],
    items: [
      { step: '01', icon: <IconPdf />, label: 'Import Sage', text: 'PDF exportés Sage X3 — reconnaissance automatique du client' },
      { step: '02', icon: <IconUsers />, label: 'Référentiel', text: 'Clients convention, emails et matricules fiscaux' },
      { step: '03', icon: <IconMail />, label: 'Campagnes', text: 'Envoi des conventions PDF par email en masse' },
    ],
  },
]

const TRUST_ITEMS = [
  { value: '2', label: 'Modules métier' },
  { value: '1', label: 'Plateforme unifiée' },
  { value: '100%', label: 'Traçabilité PDF' },
]

const ROLE_HINTS = [
  { label: 'RH', desc: 'Coaches & import Excel' },
  { label: 'Corporate', desc: 'PDF Sage & conventions' },
  { label: 'Admin', desc: 'Accès complet' },
]

const Login = () => {
  const [formData, setFormData] = useState(initialState)
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const dispatch = useDispatch()
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const user = JSON.parse(localStorage.getItem('profile'))
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail')
    if (savedEmail) {
      setFormData((prev) => ({ ...prev, email: savedEmail }))
      setRememberMe(true)
    }
  }, [])

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (loading) return
    setLoading(true)

    if (rememberMe) {
      localStorage.setItem('rememberedEmail', formData.email)
    } else {
      localStorage.removeItem('rememberedEmail')
    }

    dispatch(signin(formData, openSnackbar, setLoading))
  }

  if (user) {
    const role = user?.result?.role
    history.push(role === 'corporate' ? '/corporate/dashboard' : '/dashboard')
    return null
  }

  return (
    <div className={styles.layout}>
      <div className={styles.leftPanel}>
        <div className={styles.leftOverlay}>
          <div className={styles.leftOrbs} aria-hidden="true">
            <span className={styles.orbRh} />
            <span className={styles.orbCorp} />
          </div>

          <div className={styles.leftContent}>
            <header className={`${styles.leftHero} ${styles.fadeUp}`} style={{ animationDelay: '0.05s' }}>
              <div className={styles.brandRow}>
                <img src="/favicon.png" alt="" className={styles.heroLogo} />
                <span className={styles.brandPill}>CG Facturation</span>
              </div>
              <h1 className={styles.leftTitle}>California Gym</h1>
              <p className={styles.leftSubtitle}>
                Une plateforme unifiée pour la{' '}
                <span className={styles.highlightRh}>facturation RH</span>
                {' '}et les{' '}
                <span className={styles.highlightCorp}>conventions Corporate</span>
              </p>
            </header>

            <div className={`${styles.platformBridge} ${styles.fadeUp}`} style={{ animationDelay: '0.18s' }} aria-hidden="true">
              <span className={`${styles.bridgeNode} ${styles.bridgeRh}`}>RH Coaches</span>
              <span className={styles.bridgeLine}>
                <span className={styles.bridgeDot} />
              </span>
              <span className={styles.bridgeHub}>CG</span>
              <span className={styles.bridgeLine}>
                <span className={styles.bridgeDot} />
              </span>
              <span className={`${styles.bridgeNode} ${styles.bridgeCorp}`}>Corporate</span>
            </div>

            <div className={styles.modulesGrid}>
              {MODULES.map((mod, cardIndex) => (
                <article
                  key={mod.id}
                  className={`${styles.moduleCard} ${styles[`module${mod.accent === 'rh' ? 'Rh' : 'Corporate'}`]}`}
                  style={{ animationDelay: `${cardIndex * 0.12}s` }}
                >
                  <div className={styles.moduleTop}>
                    <div className={styles.moduleIconLarge}>{mod.items[0].icon}</div>
                    <div className={styles.moduleMeta}>
                      <span className={styles.moduleBadge}>{mod.badge}</span>
                      <h2>{mod.title}</h2>
                    </div>
                  </div>

                  <div className={styles.moduleFlow}>
                    {mod.flow.map((step, i) => (
                      <React.Fragment key={step}>
                        <span>{step}</span>
                        {i < mod.flow.length - 1 && <span className={styles.flowArrow}>→</span>}
                      </React.Fragment>
                    ))}
                  </div>

                  <ul className={styles.moduleList}>
                    {mod.items.map((item) => (
                      <li key={item.step}>
                        <span className={styles.stepNum}>{item.step}</span>
                        <div className={styles.stepBody}>
                          <strong>{item.label}</strong>
                          <span>{item.text}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>

            <div className={styles.trustBar}>
              {TRUST_ITEMS.map((item, i) => (
                <div
                  key={item.label}
                  className={`${styles.trustItem} ${styles.fadeUp}`}
                  style={{ animationDelay: `${0.55 + i * 0.08}s` }}
                >
                  <span className={styles.trustValue}>{item.value}</span>
                  <span className={styles.trustLabel}>{item.label}</span>
                </div>
              ))}
            </div>

            <p className={`${styles.leftFooter} ${styles.fadeUp}`} style={{ animationDelay: '0.78s' }}>
              Connexion sécurisée · Redirection automatique selon votre rôle
            </p>
          </div>
        </div>
      </div>

      <div className={styles.rightPanel}>
        <div className={styles.rightInner}>
          <div className={`${styles.topBrand} ${styles.fadeUp}`} style={{ animationDelay: '0.1s' }}>
            <img src="/cg-logo.png" alt="California Gym" className={styles.logo} />
            <p className={styles.tagline}>RH Coaches · Corporate Conventions</p>
          </div>

          <div className={styles.mobileModules} aria-hidden="true">
            {MODULES.map((mod) => (
              <div
                key={mod.id}
                className={`${styles.mobileModulePill} ${styles[`pill${mod.accent === 'rh' ? 'Rh' : 'Corporate'}`]}`}
              >
                <strong>{mod.badge}</strong>
                <span>{mod.title}</span>
              </div>
            ))}
          </div>

          <div className={`${styles.formCard} ${styles.slideInRight}`} style={{ animationDelay: '0.22s' }}>
            <h2 className={styles.formTitle}>Bienvenue</h2>
            <p className={styles.formSubtitle}>
              Connectez-vous — votre espace s&apos;ouvre selon votre profil
            </p>

            <div className={styles.roleHints}>
              {ROLE_HINTS.map((hint, i) => (
                <div
                  key={hint.label}
                  className={`${styles.roleHint} ${styles.fadeUp}`}
                  style={{ animationDelay: `${0.38 + i * 0.07}s` }}
                >
                  <span className={styles.roleHintLabel}>{hint.label}</span>
                  <span className={styles.roleHintDesc}>{hint.desc}</span>
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label htmlFor="email">Adresse email</label>
                <div className={styles.inputWrap}>
                  <span className={styles.inputIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="password">Mot de passe</label>
                <div className={styles.inputWrap}>
                  <span className={styles.inputIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Votre mot de passe"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Afficher le mot de passe"
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className={styles.formOptions}>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  Se souvenir de moi
                </label>
                <Link to="/forgot" className={styles.forgotLink}>
                  Mot de passe oublié ?
                </Link>
              </div>

              <button type="submit" className={styles.submitBtn} disabled={loading}>
                {loading ? (
                  <span className={styles.spinner} />
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    Se connecter
                  </>
                )}
              </button>

              <p className={styles.adminNote}>
                Accès sur invitation uniquement. Contactez votre administrateur pour obtenir un compte RH, Corporate ou Admin.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
